// src/pages/ServicePages/EventDecorAndStylingPage.jsx

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactWidget from '../../components/ui/ContactWidget';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ClientLogos from '../homepage/components/ClientLogos';
import DecorQuoteModal from '../../components/ui/DecorQuoteModal.jsx'; // <-- IMPORT THE NEW MODAL

// Reusable Gallery Image Component
const GalleryImage = ({ src, alt }) => (
  <div className="overflow-hidden rounded-xl group shadow-sm hover:shadow-lg transition-shadow duration-300">
    <img 
      src={src} 
      alt={alt}
      className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-500 ease-in-out" 
      loading="lazy"
    />
  </div>
);

// Reusable FAQ Item Component
const FaqItem = ({ question, children }) => (
  <details className="p-6 rounded-lg bg-white border border-gray-200/80 cursor-pointer group transition-colors hover:bg-gray-50">
    <summary className="font-semibold text-lg flex items-center justify-between">
      {question}
      <Icon name="ChevronDown" className="h-5 w-5 transition-transform duration-300 group-open:rotate-180" />
    </summary>
    <div className="text-gray-600 mt-4 leading-relaxed">
      {children}
    </div>
  </details>
);

const EventDecorAndStylingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const galleryItems = [
    { src: '/gallery/decor1.jpg', alt: 'Breathtaking wedding aisle with floral arrangements in Nairobi.' },
    { src: '/gallery/decor2.jpg', alt: 'Luxury tablescape design with custom centerpieces for a gala in Kenya.' },
    { src: '/gallery/decor3.jpg', alt: 'Ambient event lighting creating a magical atmosphere for a celebration.' },
    { src: '/gallery/decor4.jpg', alt: 'Custom corporate stage backdrop and branding in Nairobi.' },
    { src: '/gallery/decor5.jpg', alt: 'Chic lounge furniture arrangement for a VIP event.' },
    { src: '/gallery/decor6.jpg', alt: 'Elaborate themed party decor for a private celebration.' },
    { src: '/gallery/decor7.jpg', alt: 'Beautifully decorated floral arch for an outdoor wedding ceremony.' },
    { src: '/gallery/decor8.jpg', alt: 'Elegant entrance draping and styling for a luxury event.' },
  ];
  
  // SEO: Structured Data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Luxe & Allure Event Decor & Styling",
    "image": "https://www.yourwebsite.com/gallery/wedding.jpg", // Replace with absolute URL
    "@id": "https://www.yourwebsite.com/services/event-decor-styling", // Replace with absolute URL
    "url": "https://www.yourwebsite.com/services/event-decor-styling", // Replace with absolute URL
    "telephone": "+254727937010",
    "priceRange": "$$$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "123 Creative Lane",
      "addressLocality": "Nairobi",
      "addressCountry": "KE"
    },
    "description": "Bespoke event decor and styling services in Nairobi, Kenya. We create immersive and breathtaking environments for luxury weddings, corporate galas, and private celebrations.",
    "serviceType": "Event Decorator",
    "areaServed": {
      "@type": "Country",
      "name": "Kenya"
    }
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50 text-gray-800">
        <Helmet>
          <title>Luxury Event Decor & Styling in Nairobi, Kenya | Luxe & Allure</title>
          <meta name="description" content="Award-winning event decor and styling in Nairobi. We create breathtaking environments for luxury weddings and corporate events through bespoke floral design, lighting, and styling." />
          <link rel="canonical" href="https://www.yourwebsite.com/services/event-decor-styling" />
          <meta name="keywords" content="event decor nairobi, wedding styling kenya, floral design nairobi, luxury event decor, theme conceptualization, tablescape design kenya, event decorator nairobi" />
          <meta property="og:title" content="Luxury Event Decor & Styling in Nairobi, Kenya | Luxe & Allure" />
          <meta property="og:description" content="Transforming spaces into unforgettable experiences. Discover our bespoke floral, lighting, and styling services for weddings and corporate events." />
          <meta property="og:image" content="https://www.yourwebsite.com/gallery/decor/luxury-tablescape.jpg" />
          <meta property="og:url" content="https://www.yourwebsite.com/services/event-decor-styling" />
          <meta property="og:type" content="website" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <Header />
        <ContactWidget />

        <main className="font-sans">
          {/* --- Hero Section --- */}
          <section className="bg-white">
            <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="text-center lg:text-left">
                        <Breadcrumb />
                        <h1 className="font-serif text-5xl md:text-6xl font-bold leading-tight tracking-tight mt-4">
                            Expert Event Decor & Styling in Nairobi
                        </h1>
                        <p className="mt-6 text-lg text-gray-600 max-w-xl mx-auto lg:mx-0">
                            We are visual storytellers, transforming spaces into immersive environments that captivate guests and create lasting memories.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                            <Button variant="primary" size="lg" onClick={() => setIsModalOpen(true)}>
                                Request a Consultation
                            </Button>
                            <Button variant="outline" size="lg" onClick={() => document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                View Our Portfolio
                                <Icon name="ArrowDown" className="ml-2 w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    <div className="h-80 lg:h-full w-full">
                        <img src="/gallery/wedding.jpg" alt="Exquisite wedding decor and styling in Nairobi by Luxe & Allure." className="w-full h-full object-cover rounded-xl shadow-lg" />
                    </div>
                </div>
            </div>
          </section>

          {/* --- Philosophy Section --- */}
          <section className="py-20 lg:py-28">
            <div className="container mx-auto px-6 lg:px-8 text-center max-w-4xl">
              <h2 className="font-serif text-4xl font-bold text-gray-900">
                Design is the Soul of the Celebration
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                At Luxe & Allure, we believe decor is more than ornamentation; it's a narrative. It’s the silent language that speaks volumes about your personality, brand, or love story. Our passion is transforming spaces—from grand Nairobi ballrooms to rustic Rift Valley venues—into multi-sensory experiences. We craft the atmosphere that becomes the backdrop for your most cherished moments.
              </p>
            </div>
          </section>

          {/* --- Creative Process Section --- */}
          <section className="py-20 lg:py-28 bg-white">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                  <h2 className="font-serif text-4xl font-bold text-gray-900">Our Collaborative Design Process</h2>
                  <p className="mt-4 text-lg text-gray-600">From a spark of inspiration to a flawless reveal, we guide you every step of the way.</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="Sparkles" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">1. Discovery & Vision</h3>
                      <p className="text-gray-600">We begin with an in-depth consultation to understand your vision, style, and the story you want to tell.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="PenRuler" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">2. Concept & Mood Board</h3>
                      <p className="text-gray-600">Our team translates your ideas into a tangible design concept through detailed mood boards and sketches.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="Flower2" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">3. Design & Curation</h3>
                      <p className="text-gray-600">We source the finest florals, furniture, and materials, creating detailed plans and visualizations for your approval.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="MagicWand" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">4. Flawless Execution</h3>
                      <p className="text-gray-600">Our production team manages all logistics, executing the setup with precision for a perfect grand reveal.</p>
                  </div>
              </div>
            </div>
          </section>

          {/* --- Gallery Section --- */}
          <section id="gallery-section" className="py-20 lg:py-28">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="font-serif text-4xl font-bold text-gray-900">
                  Our Decor & Styling Portfolio
                </h2>
                <p className="mt-4 text-lg text-gray-600">A glimpse into the beautiful environments we've crafted.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {galleryItems.map((item, index) => ( <GalleryImage key={index} src={item.src} alt={item.alt} /> ))}
              </div>
            </div>
          </section>

          {/* --- FAQ Section --- */}
          <section className="py-20 lg:py-28 bg-white">
            <div className="container mx-auto px-6 lg:px-8">
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <h2 className="font-serif text-4xl font-bold text-gray-900">Event Decor FAQs</h2>
                    <p className="mt-4 text-lg text-gray-600">Your questions about our event styling services in Kenya, answered.</p>
                </div>
                <div className="max-w-4xl mx-auto space-y-4">
                    <FaqItem question="Do you handle decor for events outside of Nairobi?">
                      <p>Yes, absolutely. While based in Nairobi, our team is fully equipped to design and execute event decor anywhere across Kenya and East Africa. We manage all logistics, from transport to on-site setup, ensuring a seamless experience regardless of the location.</p>
                    </FaqItem>
                    <FaqItem question="Can you work with a specific theme I have in mind?">
                      <p>Definitely. We thrive on bringing unique visions to life! Whether you have a fully-formed theme, a specific color palette, or just a few inspirational images, our creative team will collaborate with you to develop a cohesive and stunning design concept that feels uniquely yours.</p>
                    </FaqItem>
                    <FaqItem question="What is your approach to floral design?">
                        <p>Our floral design is bespoke and integral to the overall event aesthetic. We source the freshest, highest-quality blooms and create arrangements that range from classic and elegant to modern and architectural. We consider seasonality, color theory, and the venue itself to craft florals that truly elevate the space.</p>
                    </FaqItem>
                </div>
            </div>
          </section>

          {/* --- Final Call to Action --- */}
          <section className="bg-primary text-white">
            <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
              <h2 className="font-serif text-4xl font-bold">
                Ready to Create a Masterpiece?
              </h2>
              <p className="mt-4 max-w-xl mx-auto text-primary/80">
                Let's discuss your vision. Contact us for a complimentary design consultation and let's begin the journey of creating your unforgettable event.
              </p>
              <Button variant="secondary" size="xl" className="mt-8" onClick={() => setIsModalOpen(true)}>
                  Request a Design Consultation
                  <Icon name="PenRuler" className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </section>

          <ClientLogos />
        </main>
      </div>
      
      {isModalOpen && <DecorQuoteModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

export default EventDecorAndStylingPage;